import { useState, useEffect, useCallback } from "react";
import clsx from "clsx";
import PageHeader from "../components/PageHeader";
import { TabBar } from "../components/molecules/TabBar";
import { useMenuTitle } from "../shared/menu/useMenuTitle";
import { fetchOrganizations, type OrganizationResponse } from "../api/organizations";
import { fetchStores, type StoreResponse } from "../api/stores";
import { OrganizationTreeView } from "../components/molecules/OrganizationTreeView";
import { LoadingIndicator } from "../components/atoms/LoadingIndicator";
import { ListIcon } from "../components/atoms/Icon";

type ViewMode = "list" | "tree";

const ViewToggle = ({ viewMode, onChange }: { viewMode: ViewMode; onChange: (mode: ViewMode) => void }) => (
    <div className="inline-flex rounded-lg overflow-hidden border border-gray-200">
        <button type="button" onClick={() => onChange("list")} className={clsx("p-2 transition-colors", viewMode === "list" ? "bg-brand-500 text-white" : "bg-white text-gray-400 hover:text-gray-600")}>
            <ListIcon className="w-5 h-5" />
        </button>
        <button type="button" onClick={() => onChange("tree")} className={clsx("p-2 transition-colors", viewMode === "tree" ? "bg-brand-500 text-white" : "bg-white text-gray-400 hover:text-gray-600")}>
            <GridIcon className="w-5 h-5" />
        </button>
    </div>
);

const OrganizationPage = () => {
    const menuTitle = useMenuTitle();
    const [viewMode, setViewMode] = useState<ViewMode>("tree");
    const [organizations, setOrganizations] = useState<OrganizationResponse[]>([]);
    const [stores, setStores] = useState<StoreResponse[]>([]);
    const [loading, setLoading] = useState(true);

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const [orgsRes, storesRes] = await Promise.all([
                fetchOrganizations(),
                fetchStores(),
            ]);
            setOrganizations(orgsRes.items);
            setStores(storesRes.items);
        } catch (err) {
            console.error("組織データの取得に失敗しました", err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadData();
    }, [loadData]);

    return (
            <div className="flex-1 flex flex-col overflow-hidden">
                <PageHeader title={menuTitle || "組織設定"} rightContent={<ViewToggle viewMode={viewMode} onChange={setViewMode} />} />
                <TabBar parentCode="org_settings" />
                <div className="flex-1 min-h-0 px-6 pb-6">
                    {viewMode === "tree" ? (
                        loading ? (
                            <LoadingIndicator />
                        ) : (
                            <div className="overflow-x-auto overflow-y-auto max-h-full">
                                <OrganizationTreeView organizations={organizations} stores={stores} onDataChange={loadData} />
                            </div>
                        )
                    ) : (
                        <div className="text-gray-500">準備中...</div>
                    )}
                </div>
            </div>
    );
};

export default OrganizationPage;

const GridIcon = (p: React.SVGProps<SVGSVGElement>) => <svg {...p} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path d="M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25a2.25 2.25 0 01-2.25-2.25V6zM13.5 15.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25A2.25 2.25 0 0113.5 18v-2.25z" /></svg>;
